// src/components/Common/ProductColorSection.js 
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  DragIndicator,
  Home as HomeIcon,
  HomeOutlined as HomeOutlinedIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ColorSelector from './ColorSelector';
import ImageUploader from './ImageUploader';
import ProductSizeManager from './ProductSizeManager';
import { useImageUpload } from '../../hooks/useImageUpload';

const ProductColorSection = ({
  color,
  colorIndex,
  activeColorIndex,
  setActiveColorIndex,
  onColorChange,
  onRemoveColor,
  onColorUpdate,
  onColorReorder,
  onHomeColorChange,
  totalColors,
  rtlTextFieldProps,
}) => {
  const { handleUploadImages, uploading, deleteImage } = useImageUpload();
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localImages, setLocalImages] = useState(color.images || []);

  // Sync local images with color.images
  useEffect(() => {
    setLocalImages(color.images || []);
  }, [color.images]);

  const handleUpload = async (files) => {
    try {
      setUploadError(null);
      setActiveColorIndex(colorIndex);
      
      console.log('Starting upload for files:', files);
      
      const urls = await handleUploadImages(files);
      console.log('Upload successful, URLs:', urls);
      
      if (urls && urls.length > 0) {
        const newImages = [...(color.images || []), ...urls];
        const updatedColor = {
          ...color,
          images: newImages
        };
        
        console.log('Updating color with new images:', updatedColor);
        
        // Update local state immediately
        setLocalImages(newImages);
        
        // Update parent component
        onColorUpdate(colorIndex, updatedColor);
      } else {
        console.warn('No URLs returned from upload');
        setUploadError('لم يتم رفع أي صور. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(`فشل في رفع الصور: ${error.message || 'خطأ غير معروف'}`);
    }
  };

  const handleImageReorder = (fromIndex, toIndex) => {
    const images = [...localImages];
    const [moved] = images.splice(fromIndex, 1);
    images.splice(toIndex, 0, moved);
    
    setLocalImages(images);
    
    const updatedColor = { ...color, images };
    onColorUpdate(colorIndex, updatedColor);
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.index === destination.index) return;
    
    handleImageReorder(source.index, destination.index);
  };

  const handleRemoveImage = async (imageIndex) => {
    try {
      const imageUrl = localImages[imageIndex];
      
      // Delete from storage
      await deleteImage(imageUrl);
      
      // Update local state
      const newImages = localImages.filter((_, i) => i !== imageIndex);
      setLocalImages(newImages);
      
      // Update parent component
      const updatedColor = {
        ...color,
        images: newImages
      };
      onColorUpdate(colorIndex, updatedColor);
      
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      setUploadError('فشل في حذف الصورة. يرجى المحاولة مرة أخرى.');
    }
  };

  const handleSizeUpdate = (sizes) => {
    const updatedColor = { ...color, sizes };
    onColorUpdate(colorIndex, updatedColor);
  };

  const handleImagePreview = (imageUrl) => {
    setImagePreview(imageUrl);
  };

  const handleConfirmDelete = (imageIndex) => {
    setDeleteConfirm(imageIndex);
  };

  const handleHomeColorToggle = () => {
    if (onHomeColorChange) {
      onHomeColorChange(colorIndex, !color.is_home_color);
    }
  };

  const isActive = activeColorIndex === colorIndex;

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2, 
        border: isActive ? '2px solid #3f51b5' : '1px solid #ddd',
        bgcolor: isActive ? 'rgba(63, 81, 181, 0.02)' : 'transparent',
        transition: 'all 0.2s ease-in-out',
        position: 'relative'
      }}
    >
      {/* Drag Handle for Color Reordering */}
      <Box sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        cursor: 'grab',
        color: 'grey.500',
        zIndex: 10,
        '&:hover': {
          color: 'grey.700'
        }
      }}>
        <DragIndicator fontSize="small" />
      </Box>

      {/* Color Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1, mr: 1 }}>
          <ColorSelector
            value={color.color_id}
            onChange={(colorId) => onColorChange(colorIndex, colorId)}
            rtlTextFieldProps={rtlTextFieldProps}
          />
        </Box>
        
        {/* Color Order and Home Color Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            ترتيب: {colorIndex + 1}
          </Typography>
          
          <Tooltip title={color.is_home_color ? "هذا هو اللون الرئيسي" : "اجعل هذا اللون رئيسياً"}>
            <IconButton
              onClick={handleHomeColorToggle}
              color={color.is_home_color ? "primary" : "default"}
              size="small"
            >
              {color.is_home_color ? <HomeIcon /> : <HomeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          
          <IconButton 
            onClick={() => onRemoveColor(colorIndex)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Home Color Indicator */}
      {color.is_home_color && (
        <Box sx={{
          mb: 2,
          p: 1,
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.200',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold', textAlign: 'right', direction: 'rtl', width: '100%' }}>
            هذا هو اللون الرئيسي الذي سيظهر في بطاقة المنتج
          </Typography>
          <HomeIcon color="primary" fontSize="small" />
        </Box>
      )}

      {/* Upload Error Alert */}
      {uploadError && (
        <Alert 
          severity="error" 
          onClose={() => setUploadError(null)}
          sx={{ mb: 2 }}
        >
          {uploadError}
        </Alert>
      )}

      {/* Image Uploader */}
      <Box sx={{ 
        border: isActive ? '2px solid #3f51b5' : '1px solid #ddd', 
        p: 1, 
        borderRadius: 1,
        bgcolor: isActive ? 'rgba(63, 81, 181, 0.05)' : 'transparent',
        position: 'relative'
      }}>
        <ImageUploader 
          onUpload={handleUpload}
          disabled={uploading}
          showProgress={true}
        />
        
        {uploading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Image Grid with Drag and Drop */}
      {localImages.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'right', direction: 'rtl' }}>
            الصور المرفوعة: {localImages.length}
          </Typography>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`images-${colorIndex}`} direction="horizontal">
              {(provided) => (
                <Box 
                  ref={provided.innerRef} 
                  {...provided.droppableProps} 
                  sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 2,
                    mt: 1
                  }}
                >
                  {localImages.map((url, imgIdx) => (
                    <Draggable key={`${colorIndex}-${imgIdx}-${url}`} draggableId={`img-${colorIndex}-${imgIdx}`} index={imgIdx}>
                      {(prov, snapshot) => (
                        <Box 
                          ref={prov.innerRef} 
                          {...prov.draggableProps}
                          sx={{ 
                            position: 'relative',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #ddd',
                            bgcolor: 'white',
                            transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                            boxShadow: snapshot.isDragging ? 3 : 1,
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {/* Main Image Badge */}
                          {imgIdx === 0 && (
                            <Box sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              zIndex: 2
                            }}>
                              الصورة الرئيسية
                            </Box>
                          )}

                          {/* Drag Handle */}
                          <Box
                            {...prov.dragHandleProps}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              borderRadius: 1,
                              p: 0.5,
                              cursor: 'grab',
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <DragIndicator fontSize="small" />
                          </Box>

                          {/* Image */}
                          <img 
                            src={url} 
                            alt={`preview-${colorIndex}-${imgIdx}`}
                            onLoad={() => console.log(`Image loaded: ${url}`)}
                            onError={(e) => {
                              console.error(`Failed to load image: ${url}`, e);
                            }}
                            style={{ 
                              width: '100%', 
                              height: 120, 
                              objectFit: 'cover',
                              display: 'block'
                            }} 
                          />

                          {/* Image Actions */}
                          <Box sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            p: 0.5
                          }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleImagePreview(url)}
                              sx={{ color: 'white' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleConfirmDelete(imgIdx)}
                              sx={{ color: 'white' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}

      {/* Size Manager */}
      <ProductSizeManager
        sizes={color.sizes || []}
        onSizeUpdate={handleSizeUpdate}
        rtlTextFieldProps={rtlTextFieldProps}
      />

      {/* Image Preview Dialog */}
      <Dialog
        open={!!imagePreview}
        onClose={() => setImagePreview(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>معاينة الصورة</DialogTitle>
        <DialogContent>
          {imagePreview && (
            <img 
              src={imagePreview} 
              alt="Preview" 
              style={{ 
                width: '100%', 
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain'
              }} 
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreview(null)}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من أنك تريد حذف هذه الصورة؟ لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>إلغاء</Button>
          <Button 
            onClick={() => handleRemoveImage(deleteConfirm)} 
            color="error"
            variant="contained"
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProductColorSection;
